import React, { useEffect, useMemo, useState } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { DatasetDataInfo, mapDatasetDataInfoResponseToDatasetDataInfo } from "models/dataset-models";
import { ProgressSpinner } from "primereact/progressspinner";
import { I, T } from "services/lang/LanguageService";
import useChartDialogStore from "store/chart-dialog-store";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import DatasetSetpointRow from "./DatasetSetpointRow";
import "./DatasetSetpointTable.scss";
import { IDatasetSetpointConfig, IDatasetSetpointRowConfig } from "./common/api/interfaces/interfaces";

export default function DatasetSetpointTable(props: IDatasetSetpointConfig) {
  const [data, setData] = useState<Array<IDatasetSetpointRowConfig>>();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [configChecked, setConfigChecked] = useState<boolean>(false);
  const [uniqueArr, setUniqueArr] = useState<Array<string>>([]);

  const { openDialog, setHeader } = useChartDialogStore();
  const selectedIdsStore = useDatasetIdentifiersStore();
  const { ids: selectedIds } = selectedIdsStore;

  useEffect(() => {
    concatArrays(tempArr => {
      const uniqueArray = [...new Set(tempArr)];
      setUniqueArr(uniqueArray);
    });
  }, [props.values]);

  const { data: recentData } = useGetRecentData(uniqueArr, {
    count: 1,
    refTime: props.referenceTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  const { data: bgDataset } = useGetRecentData([props.backgroundColor?.dataset ?? ""], {
    count: 1,
    refTime: props.referenceTime,
    options: {
      enabled: !!props.backgroundColor?.dataset,
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  useEffect(() => {
    if (configChecked) {
      return;
    }

    if (Object.entries(props).length === 0) {
      setError(T("error.no-config-have-been-provided"));
      setLoading(false);
    } else if (!props.values || props.values.length === 0) {
      setError(T("error.value-array-is-empty-or-field-is-not-provided"));
      setLoading(false);
    }

    setConfigChecked(true);
  }, [loading, props]);

  useEffect(() => {
    if (recentData) {
      remakeObjectArray(recentData);
    }
  }, [recentData]);

  const cancelSelections = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.detail === 2) {
      return;
    }
    selectedIdsStore.clear();
  };

  const concatArrays = (callback: (tempArr: Array<string>) => void) => {
    const tempArr: Array<string> = [];

    if (props.showSetpointColumn) {
      props.values.forEach(ob => {
        if (ob.dataset) {
          tempArr.push(ob.dataset);
        }
        if (ob.setpointDataset) {
          tempArr.push(ob.setpointDataset);
        }
      });
    } else {
      props.values.forEach(ob => {
        if (ob.dataset) {
          tempArr.push(ob.dataset);
        }
      });
    }
    callback(tempArr);
  };

  const remakeObjectArray = (res: Array<DatasetDataInfoResponse>) => {
    const tempArr: Array<IDatasetSetpointRowConfig> = [];
    const tempValues = [...props.values];

    tempValues.forEach(ob => {
      const tempObj: IDatasetSetpointRowConfig = {
        tag: ob.tag,
        name: ob.name,
        setpointSelected: false,
        datasetSelected: false,
        setIdentifiers() {},
      };

      if (ob.dataset) {
        const tempDatasetObject = res.find(e => e.identifier === ob.dataset);
        if (tempDatasetObject) {
          tempObj.dataset = mapDatasetDataInfoResponseToDatasetDataInfo(tempDatasetObject);
          tempObj.highlightColor = ob.highlightColor;
        }
      }
      if (ob.setpointDataset && props.showSetpointColumn) {
        const tempDatasetObject = res.find(e => e.identifier === ob.setpointDataset);
        if (tempDatasetObject) {
          tempObj.setpointDataset = mapDatasetDataInfoResponseToDatasetDataInfo(tempDatasetObject);
          tempObj.validation = ob.validation;
          tempObj.highlightColor = ob.highlightColor;
        }
      }
      tempObj.highlightColor = ob.highlightColor;
      tempArr.push(tempObj);
    });
    setData(tempArr);

    if (loading) {
      setLoading(false);
    }
  };

  const setIdentifier = (id: string, ctrlPressed: boolean, doubleClick: boolean) => {
    const getTagByIdentifier = (identifier: string): string | undefined => {
      return data?.find(
        item => item.dataset?.identifier === identifier || item.setpointDataset?.identifier === identifier
      )?.tag;
    };

    const updateHeader = (newSelectedIds: Set<string>) => {
      const tags = [...newSelectedIds].map(identifier => getTagByIdentifier(identifier)).filter(Boolean);

      const uniqueTags = [...new Set(tags)];

      if (uniqueTags.length === 1) {
        setHeader(`${T("word.tag")}: ${uniqueTags[0]}`);
      } else if (uniqueTags.length > 1) {
        setHeader(`${T("word.tags")}: ${uniqueTags.join(", ")}`);
      }
    };

    let newSelectedIds = selectedIds;

    switch (true) {
      case ctrlPressed && doubleClick:
        newSelectedIds.add(id);
        selectedIdsStore.addIdentifier(id);
        updateHeader(newSelectedIds);
        openDialog();
        break;
      case ctrlPressed && !doubleClick:
        if (!selectedIds.has(id)) {
          newSelectedIds.add(id);
          selectedIdsStore.addIdentifier(id);
        } else {
          newSelectedIds.delete(id);
          selectedIdsStore.removeIdentifier(id);
        }
        updateHeader(newSelectedIds);
        break;
      case !ctrlPressed && doubleClick:
        newSelectedIds = new Set([id]);
        selectedIdsStore.clear();
        selectedIdsStore.addIdentifier(id);
        updateHeader(newSelectedIds);
        openDialog();
        break;
      default:
        break;
    }
  };

  const printableCheck = (
    tag: string | undefined,
    name: string | undefined,
    dataset: DatasetDataInfo | undefined,
    setpointDataset: DatasetDataInfo | undefined
  ) => {
    if (!name && !dataset) {
      if (!props.showSetpointColumn && !props.showTagColumn) {
        return false;
      }
      if (!props.showSetpointColumn && props.showTagColumn && !tag) {
        return false;
      }
      if (props.showSetpointColumn && !props.showTagColumn && !setpointDataset) {
        return false;
      }
    }
    return true;
  };

  const { bgColor, opacity } = useMemo(() => {
    if (
      props.backgroundColor &&
      props.backgroundColor.dataset &&
      props.backgroundColor.conditionalColors.length
    ) {
      const bgData = bgDataset?.[0]?.data;
      if (bgData) {
        const color = props.backgroundColor.conditionalColors.find(
          condition => condition.value === bgData[0].value
        );
        return { bgColor: color?.color || "transparent", opacity: color?.opacity || 0.1 };
      }
    }
    return { bgColor: "transparent", opacity: 0 };
  }, [bgDataset, props.backgroundColor]);

  if (loading) {
    return (
      <ProgressSpinner
        style={{
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      />
    );
  }

  if (error.length > 0) {
    return <div className="main-window">{error}</div>;
  }

  return (
    <div className="main-main-window">
      {!props.reportMode && (
        <div
          className="absolute"
          style={{ inset: 0, backgroundColor: bgColor, opacity: opacity, zIndex: 0 }}
        />
      )}
      {props && props.values.length > 0 && (
        <div onClick={cancelSelections}>
          <table className="main-window relative z-1">
            <tbody>
              {props.showTableTitles && !props.hideHeader && (
                <tr id="table-head">
                  {props.showTagColumn === true && <th>{I(props.tableTitles?.tagTitle || "")}</th>}
                  <th>{I(props.tableTitles?.datasetTitle || "")}</th>
                  <th>{I(props.tableTitles?.datasetValueTitle || "")}</th>
                  {props.showSetpointColumn === true && (
                    <th>{I(props.tableTitles?.setpointValueTitle || "")}</th>
                  )}
                </tr>
              )}
              {data?.map(
                (value: IDatasetSetpointRowConfig, index: number, array: IDatasetSetpointRowConfig[]) => {
                  const nameBeforeWasEmpty = index >= 1 ? array[index - 1]?.name === " " : false;
                  if (printableCheck(value.tag, value.name, value.dataset, value.setpointDataset)) {
                    return (
                      <DatasetSetpointRow
                        showSetpointColumn={props.showSetpointColumn}
                        showTagColumn={props.showTagColumn}
                        compactMode={props.compactMode ? props.compactMode : false}
                        tag={value.tag}
                        name={value.name}
                        nameBeforeWasEmpty={nameBeforeWasEmpty}
                        setpointDataset={value.setpointDataset}
                        dataset={value.dataset}
                        datasetSelected={selectedIds.has(value.dataset ? value.dataset.identifier : "")}
                        setpointSelected={selectedIds.has(
                          value.setpointDataset ? value.setpointDataset.identifier : ""
                        )}
                        setIdentifiers={setIdentifier}
                        validation={value.validation}
                        highlightColor={value.highlightColor}
                        key={value.dataset?.identifier ?? index}
                      />
                    );
                  }
                  return null;
                }
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
